import { parseCSV } from '@theme/utilities';

/**
 * @typedef {Window & {
 *   sizeGuideIsMetric?: boolean;
 *   convertTableUnits?: () => void;
 * }} SizeGuideWindow
 */

/**
 * Read merged measurement tooltips from the JSON script #sg-size-guide-data.
 * @returns {Record<string, { measurement_guide?: string | null; measurement_copy?: string | null }>}
 */
/**
 * @typedef {{ id?: string; headline?: string; image?: string | null; contentHtml?: string }} HowToMeasureEntry
 */

/**
 * @typedef {{ image?: string | null; contentHtml?: string }} SizeGuideMeasureEntry
 */

/**
 * Read full size guide data from the JSON script #sg-size-guide-data.
 * @typedef {{ heading: string; copy: string }} GuideFaqItem
 */

/**
 * Read full size guide data from the JSON script #sg-size-guide-data.
 * @returns {{ howToMeasureHeading?: string; howToMeasureSubheading?: string; measurementTooltips?: object; howToMeasure?: Record<string, HowToMeasureEntry>; guideFaqs?: Record<string, GuideFaqItem[]>; sizeGuideMeasure?: Record<string, SizeGuideMeasureEntry> } | null}
 */
function getSizeGuideData() {
  if (typeof document === 'undefined') return null;
  var el = document.getElementById('sg-size-guide-data');
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function getMeasurementTooltips() {
  var data = getSizeGuideData();
  if (!data || !data.measurementTooltips) return {};
  /** @type {Record<string, { measurement_guide?: string | null; measurement_copy?: string | null }>} */
  var tooltips = {};
  var mt = /** @type {{ measurements?: Array<{key: string, value: object}> }} */ (
    data.measurementTooltips
  );
  (mt.measurements || []).forEach(
    /** @param {{ key: string, value: { measurement_guide?: string | null, measurement_copy?: string | null, product_types?: string[] } }} p */
    function (p) {
      tooltips[p.key] = p.value;
    },
  );
  return tooltips;
}

/**
 * Convert inches to centimetres. Rounds to 1 decimal place.
 * @param {number} inches
 * @returns {string}
 */
export function inchesToCm(inches) {
  return (inches * 2.54).toFixed(1);
}

/**
 * Convert centimetres to inches. Rounds to 1 decimal place.
 * @param {number} cm
 * @returns {string}
 */
export function cmToInches(cm) {
  return (cm / 2.54).toFixed(1);
}

(function () {
  /**
   * @param {string} s
   * @returns {string}
   */
  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * @param {Element | Document | null | undefined} scope
   * @returns {Element | Document}
   */
  function getSizeGuideRoot(scope) {
    if (scope instanceof Element && scope.matches('dialog, [data-size-guide]')) {
      return scope;
    }
    if (scope instanceof Element) {
      var parentRoot = scope.closest('dialog, [data-size-guide]');
      if (parentRoot) return parentRoot;
    }
    var activeRoot = document.querySelector(
      'dialog#sizeGuide[open], dialog.size-guide-dialog[open], [data-size-guide="page"]',
    );
    return activeRoot || document;
  }

  /**
   * @param {Element | Document | null | undefined} scope
   * @returns {string}
   */
  function getCountryCode(scope) {
    var root = getSizeGuideRoot(scope);
    var fromRoot =
      root instanceof Element ? (root.getAttribute('data-country-code') || '').trim() : '';
    var fromShopify = (window.Shopify && window.Shopify.country) || '';
    return (fromShopify || fromRoot || 'US').toUpperCase();
  }

  /**
   * @param {string} header
   * @returns {string}
   */
  function normalizeCountryHeader(header) {
    var text = String(header || '')
      .trim()
      .toUpperCase();
    if (!text) return '';
    var token = text.match(/\b([A-Z]{2})\b/);
    if (token && token[1]) return token[1];
    var leading = text.match(/^([A-Z]{2})/);
    if (leading && leading[1]) return leading[1];
    return text.replace(/[^A-Z]/g, '');
  }

  /**
   * @param {string} countryCode
   * @returns {string[]}
   */
  function getCountryAliases(countryCode) {
    var code = String(countryCode || '').toUpperCase();
    if (code === 'GB' || code === 'UK') return ['GB', 'UK'];
    return [code];
  }

  /**
   * Match full country tokens in headers such as "UK/AU".
   * @param {string} header
   * @param {string} countryCode
   * @returns {boolean}
   */
  function headerMatchesCountry(header, countryCode) {
    var text = String(header || '').toUpperCase();
    var aliases = getCountryAliases(countryCode);
    for (var i = 0; i < aliases.length; i++) {
      var alias = aliases[i];
      if (!alias) continue;
      var pattern = new RegExp('(^|[^A-Z])' + alias + '([^A-Z]|$)');
      if (pattern.test(text)) return true;
    }
    return false;
  }

  /**
   * @param {string} header
   * @returns {'us_size' | 'us_alpha' | ''}
   */
  function getUsHeaderType(header) {
    var text = String(header || '')
      .trim()
      .toUpperCase();
    if (text.indexOf('US SIZE') !== -1) return 'us_size';
    if (text.indexOf('US ALPHA') !== -1) return 'us_alpha';
    return '';
  }

  /**
   * @param {string[]} headers
   * @param {string} countryCode
   * @returns {number}
   */
  function getPriorityColumnIndex(headers, countryCode) {
    var target = (countryCode || 'US').toUpperCase();
    var idx = headers.findIndex(function (h) {
      return headerMatchesCountry(h, target);
    });
    if (idx !== -1) return idx;
    if (target !== 'US') {
      return headers.findIndex(function (h) {
        return headerMatchesCountry(h, 'US');
      });
    }
    return -1;
  }

  /**
   * @param {string[]} headers
   * @param {string} countryCode
   * @returns {{ order: number[], geoIndex: number }}
   */
  function buildConversionColumnOrder(headers, countryCode) {
    var usSizeIdx = headers.findIndex(function (h) {
      return getUsHeaderType(h) === 'us_size';
    });
    var usAlphaIdx = headers.findIndex(function (h) {
      return getUsHeaderType(h) === 'us_alpha';
    });
    var geoIdx = getPriorityColumnIndex(headers, countryCode);

    /** @type {number[]} */
    var order = [];
    /** @param {number} idx */
    function pushIdx(idx) {
      if (idx < 0) return;
      if (order.indexOf(idx) !== -1) return;
      order.push(idx);
    }
    pushIdx(usSizeIdx);
    pushIdx(usAlphaIdx);
    pushIdx(geoIdx);
    for (var i = 0; i < headers.length; i++) {
      pushIdx(i);
    }
    return { order: order, geoIndex: geoIdx };
  }

  /**
   * @param {string | null | undefined} csvData
   * @param {string[]} [activeProductTypes]
   * @param {Element | Document} [scope]
   */
  function renderSizeGuideTable(csvData, activeProductTypes, scope) {
    var table = scope
      ? scope.querySelector('#sizeGuideTable')
      : document.getElementById('sizeGuideTable');
    if (!table || !csvData) return;
    var root = table.closest('dialog, [data-size-guide]');
    var showSizePrefix = root ? root.getAttribute('data-show-size-prefix') !== 'false' : true;
    var data = parseCSV(csvData);
    if (data.length === 0) return;

    var firstRow = data[0];
    if (!firstRow) return;
    var numCols = firstRow.length;
    var numRows = data.length;
    var transposed = [];
    for (var c = 0; c < numCols; c++) {
      var newRow = [];
      for (var r = 0; r < numRows; r++) {
        newRow.push(data[r]?.[c] ?? '');
      }
      transposed.push(newRow);
    }

    var shouldSplitFirstColumn = false;
    for (var i = 1; i < transposed.length; i++) {
      var val = transposed[i]?.[0] ?? '';
      if (val.indexOf(',') !== -1) {
        shouldSplitFirstColumn = true;
        break;
      }
    }
    table.setAttribute('data-size-columns', shouldSplitFirstColumn ? '2' : '1');

    var html = '<thead><tr>';
    var headers = transposed[0];
    if (!headers) return;
    var firstHeader = headers[0] || '';
    var displayHeader = firstHeader.toLowerCase() === 'us size' ? 'Size' : firstHeader;
    if (shouldSplitFirstColumn) {
      html += '<th colspan="2">' + displayHeader + '</th>';
    } else {
      html += '<th>' + displayHeader + '</th>';
    }

    var tooltips = getMeasurementTooltips();
    for (var h = 1; h < headers.length; h++) {
      var columnName = (headers[h] || '').trim();
      var columnKey = columnName.toLowerCase().trim();
      var tooltipData = tooltips[columnKey];
      var hasTooltip =
        tooltipData && (tooltipData.measurement_guide || tooltipData.measurement_copy);
      if (
        hasTooltip &&
        tooltipData.product_types &&
        tooltipData.product_types.length > 0 &&
        activeProductTypes &&
        activeProductTypes.length > 0
      ) {
        hasTooltip = tooltipData.product_types.some(function (pt) {
          return activeProductTypes.indexOf(pt) !== -1;
        });
      }
      html += '<th>' + columnName;
      if (hasTooltip) {
        html +=
          ' <button type="button" class="sg-th-tooltip-trigger" data-sg-tooltip-key="' +
          escapeAttr(columnKey) +
          '" aria-label="More info about ' +
          escapeAttr(columnName) +
          '"></button>';
      }
      html += '</th>';
    }
    html += '</tr></thead><tbody>';

    for (var rowIdx = 1; rowIdx < transposed.length; rowIdx++) {
      html += '<tr>';
      var rowCells = transposed[rowIdx];
      var colCount = rowCells ? rowCells.length : 0;
      for (var colIdx = 0; colIdx < colCount; colIdx++) {
        var value = (rowCells && rowCells[colIdx]) || '';
        if (colIdx === 0 && value) {
          if (shouldSplitFirstColumn) {
            var sizeParts = value.split(',').map(function (s) {
              return s.trim();
            });
            var numericSize = sizeParts[0] || '';
            var letterSize = sizeParts[1] || '';
            html +=
              '<td>' +
              (showSizePrefix ? '<span class="size-prefix">US </span>' : '') +
              numericSize +
              '</td>';
            html += '<td>' + letterSize + '</td>';
          } else {
            html +=
              '<td>' +
              (showSizePrefix ? '<span class="size-prefix">US </span>' : '') +
              value +
              '</td>';
          }
        } else {
          html += '<td>' + value + '</td>';
        }
      }
      html += '</tr>';
    }
    html += '</tbody>';
    table.innerHTML = html;
    initMeasurementTooltips(/** @type {HTMLTableElement} */ (table));
  }

  /**
   * Bind tooltip show/hide: hover on desktop, tap + close button on mobile.
   * Delegation is bound once per wrapper to avoid duplicate listeners on re-render.
   * @param {HTMLTableElement} tableEl
   */
  function initMeasurementTooltips(tableEl) {
    var wrapper = /** @type {HTMLElement | null} */ (tableEl.closest('.sg-table-wrapper'));
    var tooltip = wrapper
      ? /** @type {HTMLElement | null} */ (wrapper.querySelector('#sgMeasurementTooltip'))
      : null;
    var imageWrap = wrapper
      ? /** @type {HTMLElement | null} */ (wrapper.querySelector('#sgTooltipImageWrap'))
      : null;
    var tooltipImage = wrapper
      ? /** @type {HTMLImageElement | null} */ (wrapper.querySelector('#sgTooltipImage'))
      : null;
    var tooltipContent = wrapper
      ? /** @type {HTMLElement | null} */ (wrapper.querySelector('#sgTooltipContent'))
      : null;
    var tooltipClose = wrapper ? wrapper.querySelector('#sgTooltipClose') : null;
    if (!wrapper || !tooltip || !tooltipContent) return;
    if (wrapper.hasAttribute('data-sg-tooltip-bound')) return;
    wrapper.setAttribute('data-sg-tooltip-bound', 'true');

    var w = /** @type {HTMLElement} */ (wrapper);
    var t = /** @type {HTMLElement} */ (tooltip);
    var tc = /** @type {HTMLElement} */ (tooltipContent);

    var tooltips = getMeasurementTooltips();
    /** @type {ReturnType<typeof setTimeout> | null} */
    var hoverTimeout = null;

    /**
     * @param {HTMLElement} trigger
     */
    function showTooltip(trigger) {
      var key = trigger.getAttribute('data-sg-tooltip-key');
      var data = key ? tooltips[key] : null;
      if (!data) return;
      if (data.measurement_guide && tooltipImage && imageWrap) {
        tooltipImage.src = data.measurement_guide;
        tooltipImage.alt = trigger.getAttribute('aria-label') || '';
        imageWrap.style.display = 'block';
      } else if (imageWrap) {
        imageWrap.style.display = 'none';
      }
      tc.innerHTML = data.measurement_copy || '';
      var tw = 204;
      var triggerRect = trigger.getBoundingClientRect();
      var wrapperRect = w.getBoundingClientRect();
      var left = triggerRect.left - wrapperRect.left - tw / 2 + triggerRect.width / 2;
      var top = triggerRect.bottom - wrapperRect.top + 8;
      var maxLeft = wrapperRect.width - tw - 10;
      if (left < 10) left = 10;
      else if (left > maxLeft) left = maxLeft;
      t.style.left = left + 'px';
      t.style.top = top + 'px';
      t.classList.add('active');
      t.setAttribute('aria-hidden', 'false');
    }

    function hideTooltip() {
      t.classList.remove('active');
      t.setAttribute('aria-hidden', 'true');
    }

    function isDesktop() {
      return typeof window !== 'undefined' && window.innerWidth >= 1080;
    }

    if (tooltipClose) {
      tooltipClose.addEventListener('click', function (e) {
        e.stopPropagation();
        hideTooltip();
      });
    }

    w.addEventListener('click', function (e) {
      var target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
      var trigger = target && target.closest('.sg-th-tooltip-trigger');
      if (trigger) {
        e.preventDefault();
        if (t.classList.contains('active')) {
          hideTooltip();
        } else {
          showTooltip(/** @type {HTMLElement} */ (trigger));
        }
      }
    });

    w.addEventListener('mouseover', function (e) {
      if (!isDesktop()) return;
      var target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
      var trigger = target && target.closest('.sg-th-tooltip-trigger');
      if (trigger) {
        if (hoverTimeout) clearTimeout(hoverTimeout);
        hoverTimeout = null;
        showTooltip(/** @type {HTMLElement} */ (trigger));
      }
    });

    w.addEventListener('mouseout', function (e) {
      if (!isDesktop()) return;
      var ev = /** @type {MouseEvent} */ (e);
      var related = ev.relatedTarget instanceof Node ? ev.relatedTarget : null;
      if (related && (w.contains(related) || t.contains(related))) return;
      hoverTimeout = setTimeout(function () {
        var triggerHover = w.querySelector('.sg-th-tooltip-trigger:hover');
        var tooltipHover = t.matches && t.matches(':hover');
        if (!triggerHover && !tooltipHover) hideTooltip();
        hoverTimeout = null;
      }, 150);
    });

    document.addEventListener('click', function (e) {
      var target = e.target instanceof Node ? e.target : null;
      if (target && !w.contains(target) && !t.contains(target)) {
        if (t.classList.contains('active')) hideTooltip();
      }
    });
  }

  /**
   * Size column count in the measurements tbody (1 cell, or 2 when numeric + letter are split).
   * SSR tables omit data-size-columns; infer from the Size header colspan when a country column is injected.
   * @param {HTMLTableElement} table
   * @returns {number}
   */
  function getSizeGuideSizeColumnCount(table) {
    var raw = table.getAttribute('data-size-columns');
    if (raw != null && raw !== '') {
      var parsed = parseInt(raw, 10);
      if (Number.isFinite(parsed) && parsed >= 1) return Math.min(parsed, 4);
    }
    var headRow = table.tHead && table.tHead.rows[0];
    if (!headRow || headRow.cells.length === 0) return 1;
    var idx = 0;
    var firstTh = headRow.cells[0];
    if (firstTh && firstTh.getAttribute('data-sg-injected-country-column') === 'true') {
      idx = 1;
    }
    var sizeHeader = headRow.cells[idx];
    if (sizeHeader && sizeHeader.colSpan >= 2) return 2;
    return 1;
  }

  /**
   * Convert table body cells between inches and cm. Skips Size column(s) and injected country size cells.
   * Uses data-original-value to store the initial value so we can toggle back and forth.
   * @param {Element | Document} [scope]
   */
  function convertTableUnits(scope) {
    var tableEl = scope
      ? scope.querySelector('#sizeGuideTable')
      : document.getElementById('sizeGuideTable');
    if (!tableEl) return;
    var table = /** @type {HTMLTableElement} */ (tableEl);
    var win = /** @type {SizeGuideWindow} */ (window);
    var isMetric = Boolean(win.sizeGuideIsMetric);
    var tbody = table.tBodies[0];
    if (!tbody) return;
    var sizeColCount = getSizeGuideSizeColumnCount(table);
    var rows = tbody.rows;
    for (var r = 0; r < rows.length; r++) {
      var row = rows[r];
      if (!row) continue;
      var cells = row.cells;
      var c = 0;
      while (c < cells.length) {
        var lead = cells[c];
        if (!lead) break;
        if (lead.getAttribute('data-sg-conversion-size') === 'true') {
          c += 1;
          continue;
        }
        break;
      }
      var startMeasurement = c + sizeColCount;
      for (c = startMeasurement; c < cells.length; c++) {
        var cell = cells[c];
        if (!cell) continue;
        var text = cell.textContent || '';
        var original = cell.getAttribute('data-original-value');
        if (original == null) {
          cell.setAttribute('data-original-value', text);
          original = text;
        }
        if (isMetric) {
          cell.textContent = original.replace(
            /(\d+\.?\d*)/g,
            /** @param {string} match */ function (match) {
              var num = parseFloat(match);
              if (Number.isNaN(num)) return match;
              return inchesToCm(num);
            },
          );
        } else {
          cell.textContent = original;
        }
      }
    }
  }

  /** @type {SizeGuideWindow} */ (window).convertTableUnits = convertTableUnits;

  /**
   * Render the international conversion table from CSV.
   *
   * Supports two formats:
   *
   * Row-first (countries as header row):
   *   US,UK,EU,AU
   *   XS (0-2),6-8,32-34,6-8
   *   → first row becomes <thead>, subsequent rows become <tbody> rows.
   *
   * Column-first (countries as first column, transposed):
   *   US Size,"00, 24","0, 25",...
   *   US Alpha,XXS,XS,...
   *   IT,34,36,...
   *   → first column becomes <thead>, subsequent columns become <tbody> rows.
   *
   * Detection: if the second cell of the first row contains a digit it is column-first
   * (size values sit in data[0][1+]); otherwise it is row-first (country codes have no digits).
   *
   * @param {string} csvData
   * @param {Element | Document} [scope]
   */
  function renderConversionTable(csvData, scope) {
    var root = getSizeGuideRoot(scope);
    var table = root.querySelector('#conversionTable');
    if (!table || !csvData) return;
    var data = parseCSV(csvData);
    if (data.length === 0) return;
    var firstRow = data[0];
    if (!firstRow || firstRow.length === 0) return;

    var secondCell = String(firstRow[1] || '').trim();
    var isColumnFirst = /\d/.test(secondCell);
    /** @type {string[]} */
    var headers = [];
    /** @type {string[][]} */
    var bodyRows = [];

    if (isColumnFirst) {
      var cfNumRows = data.length;
      var cfNumCols = firstRow.length;
      for (var cfR = 0; cfR < cfNumRows; cfR++) {
        var cfHeaderRow = data[cfR];
        headers.push(String((cfHeaderRow && cfHeaderRow[0]) || '').trim());
      }
      for (var cfC = 1; cfC < cfNumCols; cfC++) {
        var row = [];
        for (var cfRi = 0; cfRi < cfNumRows; cfRi++) {
          var cfBodyRow = data[cfRi];
          row.push(String((cfBodyRow && cfBodyRow[cfC]) || '').trim());
        }
        bodyRows.push(row);
      }
    } else {
      headers = firstRow.map(function (h) {
        return String(h || '').trim();
      });
      for (var rfR = 1; rfR < data.length; rfR++) {
        var rfRow = data[rfR];
        if (!rfRow) continue;
        bodyRows.push(
          rfRow.map(function (cell) {
            return String(cell || '').trim();
          }),
        );
      }
    }

    var countryCode = getCountryCode(root);
    var columnOrderData = buildConversionColumnOrder(headers, countryCode);
    var orderedIndices = columnOrderData.order;
    var orderedGeoSourceIdx = columnOrderData.geoIndex;
    headers = orderedIndices.map(function (idx) {
      return headers[idx] || '';
    });
    bodyRows = bodyRows.map(function (row) {
      return orderedIndices.map(function (idx) {
        return (row && row[idx]) || '';
      });
    });
    table.setAttribute(
      'data-conversion-source-index',
      String(Math.max(0, orderedIndices.indexOf(orderedGeoSourceIdx))),
    );

    var html = '<thead><tr>';
    for (var h = 0; h < headers.length; h++) {
      html += '<th>' + headers[h] + '</th>';
    }
    html += '</tr></thead><tbody>';
    for (var r = 0; r < bodyRows.length; r++) {
      var rowCells = bodyRows[r];
      if (!rowCells) continue;
      html += '<tr>';
      for (var c = 0; c < rowCells.length; c++) {
        html += '<td>' + rowCells[c] + '</td>';
      }
      html += '</tr>';
    }
    html += '</tbody>';
    table.innerHTML = html;
  }

  /**
   * For non-US visitors, mirror conversion table first column into measurements first data column.
   * @param {Element | Document} [scope]
   */
  function syncMeasurementsFirstColumnFromConversion(scope) {
    var root = getSizeGuideRoot(scope);
    var measurementsTableEl = root.querySelector('#sizeGuideTable');
    var conversionTableEl = root.querySelector('#conversionTable');
    if (
      !(measurementsTableEl instanceof HTMLTableElement) ||
      !(conversionTableEl instanceof HTMLTableElement)
    )
      return;
    var measurementsTable = measurementsTableEl;
    var conversionTable = conversionTableEl;

    function removeInjectedMeasurementCountryColumn() {
      var headerRow = measurementsTable.tHead && measurementsTable.tHead.rows[0];
      if (headerRow && headerRow.cells[0]) {
        var firstHeaderCell = headerRow.cells[0];
        if (
          firstHeaderCell &&
          firstHeaderCell.getAttribute('data-sg-injected-country-column') === 'true'
        ) {
          firstHeaderCell.remove();
        }
      }
      var body = measurementsTable.tBodies[0];
      if (!body) return;
      for (var rowIdx = 0; rowIdx < body.rows.length; rowIdx++) {
        var row = body.rows[rowIdx];
        if (!row || !row.cells[0]) continue;
        var firstCell = row.cells[0];
        if (firstCell && firstCell.getAttribute('data-sg-injected-country-column') === 'true') {
          firstCell.remove();
        }
      }
    }

    if (getCountryCode(root) === 'US') {
      removeInjectedMeasurementCountryColumn();
      return;
    }

    var hasConversionRows =
      conversionTable.tBodies &&
      conversionTable.tBodies[0] &&
      conversionTable.tBodies[0].rows &&
      conversionTable.tBodies[0].rows.length > 0;
    if (!hasConversionRows) {
      var conversionContent = root.querySelector('#conversion-content');
      var csvData = conversionContent
        ? conversionContent.getAttribute('data-conversion-csv')
        : null;
      if (csvData) renderConversionTable(csvData, root);
    }

    var measureBody = measurementsTable.tBodies[0];
    var conversionBody = conversionTable.tBodies[0];
    if (!measureBody || !conversionBody) return;
    removeInjectedMeasurementCountryColumn();

    var sourceIndex = parseInt(
      conversionTable.getAttribute('data-conversion-source-index') || '0',
      10,
    );
    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) sourceIndex = 0;

    var measureHeaderRow = measurementsTable.tHead && measurementsTable.tHead.rows[0];
    var conversionHeaderRow = conversionTable.tHead && conversionTable.tHead.rows[0];
    if (
      measureHeaderRow &&
      conversionHeaderRow &&
      sourceIndex < conversionHeaderRow.cells.length
    ) {
      var sourceHeaderCell = conversionHeaderRow.cells[sourceIndex];
      if (sourceHeaderCell) {
        var insertedHeaderCell = document.createElement('th');
        insertedHeaderCell.setAttribute('data-sg-injected-country-column', 'true');
        insertedHeaderCell.textContent = sourceHeaderCell.textContent || '';
        if (measureHeaderRow.firstChild) {
          measureHeaderRow.insertBefore(insertedHeaderCell, measureHeaderRow.firstChild);
        } else {
          measureHeaderRow.appendChild(insertedHeaderCell);
        }
      }
    }

    var rowCount = Math.min(measureBody.rows.length, conversionBody.rows.length);
    for (var rowIdx = 0; rowIdx < rowCount; rowIdx++) {
      var measureRow = measureBody.rows[rowIdx];
      var conversionRow = conversionBody.rows[rowIdx];
      if (!measureRow || !conversionRow) continue;
      if (conversionRow.cells.length <= sourceIndex) continue;
      var sourceCell = conversionRow.cells[sourceIndex];
      if (!sourceCell) continue;
      var insertedCell = document.createElement('td');
      insertedCell.setAttribute('data-sg-injected-country-column', 'true');
      insertedCell.setAttribute('data-sg-conversion-size', 'true');
      insertedCell.textContent = sourceCell.textContent || '';
      if (measureRow.firstChild) {
        measureRow.insertBefore(insertedCell, measureRow.firstChild);
      } else {
        measureRow.appendChild(insertedCell);
      }
    }
  }

  /**
   * @param {Element | Document} [scope]
   */
  function showConversionContent(scope) {
    var root = getSizeGuideRoot(scope);
    var conversionContent = root.querySelector('#conversion-content');
    var measurementsContent = root.querySelector('#measurements-content');
    var controls = root.querySelector('.sg-measurement-controls');
    if (conversionContent) conversionContent.classList.add('active');
    if (measurementsContent) measurementsContent.classList.remove('active');
    if (controls) controls.classList.add('sg-measurement-controls--hidden');
    var csv = conversionContent ? conversionContent.getAttribute('data-conversion-csv') : null;
    if (csv) renderConversionTable(csv, root);
  }

  /**
   * @param {Element | Document} [scope]
   */
  function showMeasurementsContent(scope) {
    var root = getSizeGuideRoot(scope);
    var conversionContent = root.querySelector('#conversion-content');
    var measurementsContent = root.querySelector('#measurements-content');
    var controls = root.querySelector('.sg-measurement-controls');
    if (conversionContent) conversionContent.classList.remove('active');
    if (measurementsContent) measurementsContent.classList.add('active');
    if (controls) controls.classList.remove('sg-measurement-controls--hidden');
    syncMeasurementsFirstColumnFromConversion(root);
  }

  var chartTabs = document.querySelectorAll('.size-chart-section [data-chart-tab]');
  chartTabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      var root = getSizeGuideRoot(this);
      var target = this.getAttribute('data-chart-tab');
      root.querySelectorAll('.size-chart-section [data-chart-tab]').forEach(function (t) {
        t.classList.toggle('active', t.getAttribute('data-chart-tab') === target);
      });
      if (target === 'conversion') {
        showConversionContent(root);
      } else {
        showMeasurementsContent(root);
      }
    });
  });

  /** @type {string | null} */
  var initialMeasureContent = null;

  /**
   * @param {HTMLSelectElement} selectElement
   */
  function updateHowToMeasureSection(selectElement) {
    var root = selectElement.closest('dialog, [data-size-guide]');
    var wrapper = root ? root.querySelector('.sg-how-to-measure-content-wrapper') : null;
    if (!wrapper) return;

    if (initialMeasureContent === null) {
      initialMeasureContent = wrapper.innerHTML;
    }

    var selected = selectElement.options[selectElement.selectedIndex];
    var guideId = selected ? selected.value : null;
    var howToMeasureId = selected ? selected.getAttribute('data-how-to-measure-id') : null;
    var data = getSizeGuideData();
    var entry = null;
    if (data && data.sizeGuideMeasure && guideId && data.sizeGuideMeasure[guideId]) {
      entry = data.sizeGuideMeasure[guideId];
    }
    if (
      !entry &&
      data &&
      data.howToMeasure &&
      howToMeasureId &&
      data.howToMeasure[howToMeasureId]
    ) {
      entry = data.howToMeasure[howToMeasureId];
    }

    if (!entry && data?.howToMeasure?.default) {
      entry = data.howToMeasure.default;
    }

    if (entry && (entry.image || entry.contentHtml)) {
      var parts = [];
      if (entry.image) {
        var imgSrc = String(entry.image).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        parts.push(
          '<img class="sg-how-to-measure-image" src="' +
            imgSrc +
            '" alt="" width="600" height="440" loading="lazy">',
        );
      }
      parts.push('<div class="sg-how-to-measure-text">');
      if (data && data.howToMeasureHeading !== '') {
        parts.push(
          '<h3 class="sg-how-to-measure-heading sg-how-to-measure-heading--inline h2">' +
            data.howToMeasureHeading +
            '</h3>',
        );
      }
      if (data && data.howToMeasureSubheading !== '') {
        parts.push(
          '<p class="sg-how-to-measure-subheading sg-how-to-measure-subheading--inline">' +
            data.howToMeasureSubheading +
            '</p>',
        );
      }
      if (entry.contentHtml) {
        parts.push('<div class="sg-how-to-measure-content">' + entry.contentHtml + '</div>');
      }
      parts.push('</div>');
      wrapper.innerHTML = parts.join('');
    } else if (initialMeasureContent !== null) {
      wrapper.innerHTML = initialMeasureContent;
    }
  }

  /**
   * Update conversion content CSV from selected guide (guide’s conversion_guide_csv or global fallback).
   * @param {HTMLSelectElement} selectElement
   */
  function updateConversionContent(selectElement) {
    var root = selectElement.closest('dialog, [data-size-guide]');
    var conversionContent = root ? root.querySelector('#conversion-content') : null;
    if (!conversionContent) return;

    var selected = selectElement.options[selectElement.selectedIndex];
    var guideCsv = selected ? selected.getAttribute('data-conversion-csv') : null;
    var fallbackCsv = conversionContent.getAttribute('data-conversion-csv-fallback');
    var csv = guideCsv && guideCsv.trim() !== '' ? guideCsv : fallbackCsv;
    conversionContent.setAttribute('data-conversion-csv', csv || '');

    if (conversionContent.classList.contains('active') && csv) {
      renderConversionTable(csv, root);
    }
    if (root) {
      syncMeasurementsFirstColumnFromConversion(root);
    } else {
      syncMeasurementsFirstColumnFromConversion(document);
    }
  }

  /**
   * Render per-guide FAQ accordions into #sg-guide-faqs.
   * Clones icon SVGs from an existing accordion in the same scope.
   * @param {HTMLSelectElement} selectElement
   */
  function updateFaqSection(selectElement) {
    const root = selectElement.closest('dialog, [data-size-guide]');
    if (!root) return;
    const container = root.querySelector('#sg-guide-faqs');
    if (!container) return;

    const selected = selectElement.options[selectElement.selectedIndex];
    const guideId = selected ? selected.value : null;
    const data = getSizeGuideData();
    const faqs = data && data.guideFaqs && guideId ? data.guideFaqs[guideId] : null;

    container.innerHTML = '';

    const isPage = !!root.getAttribute('data-size-guide');
    const heading = isPage ? root.querySelector('#sg-faqs-heading') : null;

    if (!faqs || faqs.length === 0) {
      if (heading) heading.setAttribute('hidden', '');
      return;
    }

    if (heading) heading.removeAttribute('hidden');

    // Grab icon SVGs from the hidden template
    const iconTemplate = root.querySelector('#sg-faq-icons');
    const iconContent = iconTemplate ? iconTemplate.content || iconTemplate : null;
    const caretSvg = iconContent ? iconContent.querySelector('.icon-caret') : null;
    const plusSvg = iconContent ? iconContent.querySelector('.icon-plus') : null;
    const minusSvg = iconContent ? iconContent.querySelector('.icon-minus') : null;

    for (let i = 0; i < faqs.length; i++) {
      const faq = faqs[i];
      if (!faq.heading) continue;

      const accordion = document.createElement('accordion-custom');
      const details = document.createElement('details');
      details.className = 'details';
      const summary = document.createElement('summary');
      summary.className = 'details__header';

      const headingSpan = document.createElement('h3');
      headingSpan.className = 'details__heading';
      headingSpan.textContent = faq.heading;
      summary.appendChild(headingSpan);

      if (caretSvg) summary.appendChild(caretSvg.cloneNode(true));
      if (plusSvg) summary.appendChild(plusSvg.cloneNode(true));
      if (minusSvg) summary.appendChild(minusSvg.cloneNode(true));

      details.appendChild(summary);

      const content = document.createElement('div');
      content.className = 'details-content';
      content.innerHTML = faq.copy || '';
      details.appendChild(content);

      accordion.appendChild(details);
      container.appendChild(accordion);
    }
  }

  /** Guide titles that should not show the IN/CM unit toggle. */
  var HIDE_UNIT_TOGGLE_GUIDES = ['dickeys', 'shoes'];

  /**
   * Show or hide the unit toggle based on the selected guide title.
   * @param {HTMLSelectElement} selectEl
   * @param {Element | Document} [scope]
   */
  function updateUnitToggleVisibility(selectEl, scope) {
    var root = scope instanceof Element ? scope : document;
    var unitToggle = root.querySelector('.sg-unit-toggle');
    if (!unitToggle) return;
    var selected = selectEl.options[selectEl.selectedIndex];
    var title = selected ? selected.textContent.trim().toLowerCase() : '';
    var shouldHide = HIDE_UNIT_TOGGLE_GUIDES.indexOf(title) !== -1;
    if (shouldHide) {
      unitToggle.setAttribute('hidden', '');
    } else {
      unitToggle.removeAttribute('hidden');
    }
  }

  /**
   * @param {Element | Document} [scope]
   */
  function updateSizeGuideFromSelect(scope) {
    var s = scope || document;
    // On standalone pages, all content is server-rendered by Liquid — skip JS updates
    if (s instanceof Element && s.getAttribute('data-size-guide') === 'page') return;
    var sel = s.querySelector('#sizeGuideSelector');
    var table = s.querySelector('#sizeGuideTable');
    if (!sel || !table) return;
    var selectEl = /** @type {HTMLSelectElement} */ (sel);
    var selected = selectEl.options[selectEl.selectedIndex];
    var csvData = selected ? selected.getAttribute('data-table') : null;
    var productTypesStr = selected ? selected.getAttribute('data-product-types') || '' : '';
    var activeProductTypes = productTypesStr
      ? productTypesStr.split(',').map(function (s) {
          return s.trim();
        })
      : [];
    renderSizeGuideTable(csvData, activeProductTypes, scope);
    updateHowToMeasureSection(selectEl);
    updateConversionContent(selectEl);
    updateFaqSection(selectEl);
    updateUnitToggleVisibility(selectEl, scope);

    if (/** @type {SizeGuideWindow} */ (window).sizeGuideIsMetric) {
      convertTableUnits(scope);
    }
    syncMeasurementsFirstColumnFromConversion(scope);
  }

  /** Expose for dialog open callback so table populates when drawer is in DOM (e.g. from quick-add template). */
  if (typeof window !== 'undefined') {
    /** @type {SizeGuideWindow & { sizeGuideOnDialogOpen?: () => void }} */
    var win = window;
    win.sizeGuideOnDialogOpen = function () {
      var dialog = document.querySelector('dialog#sizeGuide[open], dialog.size-guide-dialog[open]');
      var scope = dialog || document;
      var selEl = scope.querySelector('#sizeGuideSelector');
      var sel = selEl instanceof HTMLSelectElement ? selEl : null;
      var productType = dialog ? (dialog.getAttribute('data-product-type') || '').trim() : '';
      if (sel && productType && sel.options.length) {
        for (var i = 0; i < sel.options.length; i++) {
          var opt = sel.options[i];
          if (!opt) continue;
          var types = (opt.getAttribute('data-product-types') || '')
            .split(',')
            .map(function (/** @param {string} s */ s) {
              return s.trim();
            });
          if (types.indexOf(productType) !== -1) {
            sel.selectedIndex = i;
            break;
          }
        }
      }
      updateSizeGuideFromSelect(scope);
      // Product details and fit notes are injected by size-guide-drawer #handleDialogOpen (with wrapper for accordion)
    };
  }

  /** Event delegation: size guide may be inside a template and appear in DOM only when drawer opens. */
  document.addEventListener('change', function (e) {
    var el = e.target instanceof HTMLElement ? e.target : null;
    if (el && el.id === 'sizeGuideSelector') {
      var scope = el.closest('dialog, [data-size-guide]') || document;
      updateSizeGuideFromSelect(scope);

      // Navigate to the selected guide's page (standalone page mode only)
      if (scope instanceof Element && scope.getAttribute('data-size-guide') === 'page') {
        var selectEl = /** @type {HTMLSelectElement} */ (el);
        var selectedOpt = selectEl.options[selectEl.selectedIndex];
        var pageUrl = selectedOpt ? selectedOpt.getAttribute('data-page-url') : null;
        if (pageUrl && pageUrl !== window.location.pathname) {
          window.location.href = pageUrl;
          return;
        }
      }
    }
  });

  /** Handle browser back/forward to sync dropdown with URL. */
  window.addEventListener('popstate', function () {
    var page = document.querySelector('[data-size-guide="page"]');
    if (!page) return;
    var sel = page.querySelector('#sizeGuideSelector');
    if (!(sel instanceof HTMLSelectElement)) return;
    var currentPath = window.location.pathname;
    for (var i = 0; i < sel.options.length; i++) {
      var opt = sel.options[i];
      if (opt && opt.getAttribute('data-page-url') === currentPath) {
        sel.selectedIndex = i;
        updateSizeGuideFromSelect(page);
        return;
      }
    }
  });

  function initAllSizeGuides() {
    document.querySelectorAll('dialog#sizeGuide, [data-size-guide]').forEach(function (root) {
      if (root.querySelector('#sizeGuideSelector')) {
        updateSizeGuideFromSelect(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSizeGuides);
  } else {
    initAllSizeGuides();
  }
})();
